import { AirlineTicketClass, MainTransport } from 'common/types';
import { DistanceVO } from 'vo';
import { Co2FootprintVO } from 'vo/Co2FootprintVO';

/**
 * Per-class emission factors for aviation (kg CO₂e / passenger-km).
 *
 * Base: DEFRA/DESNZ 2025 non-UK international flight factors (with RF, 8% uplift).
 * Source: https://www.gov.uk/government/publications/greenhouse-gas-reporting-conversion-factors-2025
 *
 * Economy & Business sourced directly from DEFRA 2025.
 * First Class derived via IATA RP 1726 class ratio (4× economy).
 * Private Jet derived via industry standard ratio (8× economy).
 */
const getAirplaneEmissionFactorByClass = (ticketClass: AirlineTicketClass) => {
  const mapping: Record<AirlineTicketClass, number> = {
    [AirlineTicketClass.ECONOMY]: 0.10916, // DEFRA 2025 non-UK international economy
    [AirlineTicketClass.BUSINESS]: 0.31656, // DEFRA 2025 non-UK international business
    [AirlineTicketClass.FIRST_CLASS]: 0.43664, // Derived: economy × 4 (IATA RP 1726)
    [AirlineTicketClass.PRIVATE_JET]: 0.87328, // Derived: economy × 8
  };
  return mapping[ticketClass];
};

/**
 * Ground/sea transport emission factors (kg CO₂e / passenger-km).
 *
 * Updated January 2026.
 * Source: UK DESNZ/DEFRA Greenhouse Gas Conversion Factors 2025
 * https://www.gov.uk/government/publications/greenhouse-gas-reporting-conversion-factors-2025
 */
const getCo2ePerKmByTransport = (transport: MainTransport) => {
  const mapping: Record<MainTransport, number> = {
    [MainTransport.WALK]: 0,
    [MainTransport.CYCLE]: 0,

    [MainTransport.CAR]: 0.16272, // Petrol car average (DEFRA 2025)
    [MainTransport.BUS]: 0.10385, // Average local bus (DEFRA 2025)
    [MainTransport.TRAIN]: 0.03546, // National rail (DEFRA 2025)
    [MainTransport.ELECTRIC_CAR]: 0.04047, // Battery EV (DEFRA 2025)

    [MainTransport.HELICOPTER]: 9.8394, // BAZL helicopter emissions methodology

    // Airplane factor handled separately by getAirplaneEmissionFactorByClass
    [MainTransport.AIRPLANE]: 0,
    [MainTransport.SHIP]: 0.11286, // Ferry average per passenger (DEFRA 2025)
  };

  return mapping[transport];
};

type BaseParams = {
  peopleCount: number;
  distanceVO: DistanceVO;
};

type AirplaneParams = BaseParams & {
  ticketClass: AirlineTicketClass;
};

type ParamsByTransport<T> = T extends MainTransport.AIRPLANE ? AirplaneParams : BaseParams;

export default <T extends MainTransport>(transport: T, params: ParamsByTransport<T>) => {
  const { peopleCount, distanceVO } = params;

  const isAirplaneTransport = transport === MainTransport.AIRPLANE;

  if (isAirplaneTransport) {
    const ticketClass = (params as AirplaneParams).ticketClass;
    const perKm = getAirplaneEmissionFactorByClass(ticketClass);
    // ×2 for return trip as per methodology
    const perPerson = perKm * distanceVO.km * 2;
    const total = perPerson * peopleCount;
    return Co2FootprintVO.create({ perPerson, total });
  }

  const perKm = getCo2ePerKmByTransport(transport);
  const perPerson = perKm * distanceVO.km;
  const total = perPerson * peopleCount;

  return Co2FootprintVO.create({ perPerson, total });
};
